import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ number, title, children }) => (
  <div className="mb-8">
    <div className="flex items-start gap-3 mb-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#10A7F3] to-[#10F3D6] text-xs font-bold text-white shadow-sm shadow-[#10A7F3]/30">
        {number}
      </span>
      <h2 className="text-lg font-bold text-[#1e293b] leading-tight">{title}</h2>
    </div>
    <div className="ml-9 text-sm leading-7 text-[#2A2D34]/80 space-y-3">{children}</div>
  </div>
);

const SubSection = ({ letter, title, children }) => (
  <div className="mt-4">
    <div className="flex items-center gap-2 mb-2">
      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-[#10A7F3]/10 text-xs font-bold text-[#0D8ED3]">
        {letter}
      </span>
      <h3 className="text-sm font-semibold text-[#1e293b]">{title}</h3>
    </div>
    <div className="ml-7 text-sm leading-7 text-[#2A2D34]/80 space-y-2">{children}</div>
  </div>
);

const InfoBox = ({ children }) => (
  <div className="rounded-xl border border-[#10A7F3]/20 bg-[#10A7F3]/5 px-4 py-3 text-sm text-[#0D8ED3] leading-6">
    {children}
  </div>
);

const DataTable = ({ headers, rows }) => (
  <div className="overflow-x-auto rounded-xl border border-white/60 shadow-sm">
    <table className="w-full text-xs">
      <thead>
        <tr className="bg-gradient-to-r from-[#10A7F3]/10 to-[#10F3D6]/10 text-left">
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 font-semibold text-[#1e293b] whitespace-nowrap">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white/60' : 'bg-[#F5F7FA]/60'}>
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-[#2A2D34]/80 align-top">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Divider = () => (
  <div className="my-6 h-px w-full bg-gradient-to-r from-transparent via-[#10A7F3]/20 to-transparent" />
);

export default function Privacy() {
  return (
    <div className="relative min-h-screen bg-gradient-to-b from-[#F5F7FA] to-[#E4E7EC] text-[#2A2D34] px-4 py-12">
      {/* Decorative blobs */}
      <div className="pointer-events-none fixed -top-24 -right-28 -z-10 h-80 w-80 rounded-full bg-[#10A7F3]/20 blur-3xl" />
      <div className="pointer-events-none fixed -bottom-32 -left-24 -z-10 h-96 w-96 rounded-full bg-[#10F3D6]/20 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="mx-auto w-full max-w-3xl"
      >
        {/* Logo */}
        <div className="mb-6 flex items-center justify-between">
          <a
            href="/"
            className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent"
          >
            Flowman
          </a>
        </div>

        {/* Main card */}
        <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-8 shadow-xl backdrop-blur">
          <div className="absolute inset-0 -z-10 rounded-3xl bg-gradient-to-br from-[#10A7F3]/10 to-[#10F3D6]/20 blur-2xl" />

          {/* Header */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-white/80 px-3 py-1 text-sm font-medium text-[#0D8ED3] shadow-sm">
              <span className="inline-block h-2 w-2 rounded-full bg-[#10A7F3]" />
              Legal
            </div>
            <h1 className="mt-3 text-2xl md:text-3xl font-extrabold tracking-tight text-[#1e293b]">
              Privacy Policy
            </h1>
            <p className="mt-1 text-sm text-[#2A2D34]/60">
              Effective Date: 2026-02-22 &nbsp;·&nbsp; Last Updated: 2026-02-20
            </p>
          </div>

          <Divider />

          {/* 1. Introduction */}
          <Section number="1" title="Introduction">
            <p>
              Flowman ("we," "our," or "us") is a productivity and task management application that
              helps individuals and teams organize work, track projects, and stay on top of their
              responsibilities.
            </p>
            <p>
              <span className="font-semibold text-[#1e293b]">FlowSync</span> is a Gmail-powered AI
              feature within Flowman. When you sign up for FlowSync, you authorize Flowman to connect
              to your Gmail account and automatically scan your inbox to identify actionable emails,
              generate task suggestions, and draft reply suggestions using artificial intelligence.
            </p>
            <p>
              This Privacy Policy explains what information we collect when you use Flowman and its
              FlowSync feature, how we use and protect it, and what controls you have over your data.
              By signing up for FlowSync and connecting your Gmail account, you agree to the practices
              described in this policy.
            </p>
            <p>
              FlowSync’s use of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.
            </p>
          </Section>

          <Divider />

          {/* 2. Information We Collect */}
          <Section number="2" title="Information We Collect">

            <SubSection letter="A" title="Account Information">
              <p>When you access FlowSync, we collect:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your name and email address</li>
                <li>Your Google account identifier (OAuth provider ID)</li>
                <li>Your Google profile information retrieved via the Google OAuth consent flow</li>
              </ul>
            </SubSection>

            <SubSection letter="B" title="Gmail Data Accessed and Stored">
              <p>
              
                When you connect your Gmail account, FlowSync accesses your inbox via the Gmail API.
                The following data is <span className="font-semibold text-[#1e293b]">fetched and stored</span> in our database:
              </p>
              <p className="font-medium text-[#1e293b] mt-2">Thread and message data:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Email subject lines</li>
                <li>Email body content (plain text and HTML)</li>
                <li>Email snippets (short previews)</li>
                <li>Sender address and reply-to address</li>
                <li>Recipient addresses (To, Cc, Bcc)</li>
                <li>Email headers and timestamps</li>
                <li>Read/unread and starred status</li>
                <li>Gmail message ID, thread ID, and label names</li>
              </ul>
              <p className="font-medium text-[#1e293b] mt-3">Attachment metadata (stored):</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Attachment filename, MIME type, and file size</li>
              </ul>
              <div className="mt-3">
                <InfoBox>
                  <strong>Important:</strong> FlowSync stores attachment metadata only. Attachment
                  binary content (the actual files) is never downloaded or stored.
                </InfoBox>
              </div>
              <div className="mt-3">
                <InfoBox>
                  FlowSync does not request access to Google Calendar, Google Contacts, or any Google
                  service beyond Gmail and basic profile information.
                </InfoBox>
              </div>
            </SubSection>

            <SubSection letter="C" title="AI Processing Data">
              <p>
                To provide its core functionality, FlowSync sends portions of your email content to
                OpenAI's API. Specifically, email subject, sender address, and a body excerpt are
                sent to determine whether an email is relevant, identify actionable items and generate
                task suggestions, and generate reply draft suggestions.
              </p>
              <p className="font-medium text-[#1e293b] mt-2">What is stored from AI processing:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Task suggestions (title, description, priority, estimated due date)</li>
                <li>Reply draft suggestions (recipient, subject, body)</li>
                <li>Classification metadata (whether an email is actionable or reply-worthy)</li>
              </ul>
              <p className="font-medium text-[#1e293b] mt-2">What is not retained by FlowSync:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>The raw prompts sent to OpenAI are not independently stored by FlowSync</li>
                <li>
                  FlowSync uses the OpenAI API under terms that do not permit OpenAI to use
                  API-submitted data to train its models
                </li>
              </ul>
            </SubSection>

            <SubSection letter="D" title="Operational and Sync Data">
              <p>We store technical data necessary to operate the service:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Inbox scan job records: status, progress counts, timestamps, and error messages</li>
                <li>Email sync logs: number of emails processed, sync timestamps, and errors</li>
                <li>Rate-limit counters to enforce usage limits</li>
              </ul>
              <p>This data is used solely for service operation and troubleshooting.</p>
            </SubSection>

            <SubSection letter="E" title="Beta Program Data">
              <p>If you submit a request for beta access, we store:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Your email address</li>
                <li>Request status (pending, approved, or denied)</li>
                <li>Request and approval timestamps</li>
              </ul>
            </SubSection>

          </Section>

          <Divider />

          {/* 3. How We Use Your Information */}
          <Section number="3" title="How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                <span className="font-semibold text-[#1e293b]">Provide the core service</span> — read
                your inbox, identify actionable and reply-worthy emails, generate task suggestions,
                and draft reply suggestions.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Send emails on your behalf</span> — when
                you choose to send a draft reply, we transmit it via the Gmail API using your
                authenticated account.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Maintain your account</span> — authenticate
                you, store your preferences, and preserve your task and draft history.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Operate and improve the service</span> — diagnose
                errors, monitor performance, enforce rate limits, and ensure reliability.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Communicate with you</span> — send
                administrative emails such as beta access approval notifications.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Enforce our terms</span> — detect
                abuse, enforce rate limits, and protect the integrity of the service.
              </li>
            </ol>
            <InfoBox>
              We do not sell your data. We do not use your email content for advertising. We do not
              share your data with third parties except as described in Section 5.
            </InfoBox>
          </Section>

          <Divider />

          {/* 4. Gmail API & OAuth Permissions */}
          <Section number="4" title="Gmail API & OAuth Permissions">
            <p>
              FlowSync requests the following Google OAuth permissions when you connect your Gmail
              account:
            </p>
            <div className="mt-3">
              <DataTable
                headers={['Scope', 'Permission', 'Why We Need It']}
                rows={[
                  ['gmail.readonly', 'Read all messages and threads', 'To fetch your inbox and analyze emails'],
                  ['gmail.send', 'Send email on your behalf', 'To send reply drafts you approve'],
                  ['gmail.modify', 'Modify message labels and metadata', 'To mark emails as read and apply labels'],
                  ['userinfo.email', 'View your email address', 'To identify your connected account'],
                  ['userinfo.profile', 'View your basic profile info', 'To display your name in the app'],
                ]}
              />
            </div>
            <p className="mt-4">
              <span className="font-semibold text-[#1e293b]">Access is limited to what is necessary.</span>{' '}
              We use these permissions only to deliver the features described in this policy. We do
              not use your Gmail access for any purpose beyond what is described here.
            </p>
            <p className="font-semibold text-[#1e293b] mt-3">Revoking access</p>
            <p>You can disconnect FlowSync from your Gmail account at any time:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <span className="font-medium">Within FlowSync:</span> Use the disconnect option in
                the app's settings. This immediately revokes the OAuth token with Google and deletes
                all stored tokens from our database.
              </li>
              <li>
                <span className="font-medium">Via Google Account settings:</span> Visit{' '}
                <a
                  href="https://myaccount.google.com/permissions"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#10A7F3] hover:underline"
                >
                  myaccount.google.com/permissions
                </a>
                , find FlowSync, and remove access.
              </li>
            </ul>
            <p>
              Revoking access stops all inbox syncing. Your previously synced email data remains in
              our database until you choose to delete it (see Section 8).
            </p>
          </Section>

          <Divider />

          {/* 5. AI & Third-Party Services */}
          <Section number="5" title="AI & Third-Party Services">
            <p className="font-semibold text-[#1e293b]">OpenAI</p>
            <p>
              FlowSync uses the OpenAI API to process email content for filtering, task generation,
              and reply drafting. Email subject lines, sender addresses, and body excerpts are
              transmitted to OpenAI's servers for this processing.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Data sent to OpenAI is used solely to generate responses for the current request
              </li>
              <li>
                Per our agreement with OpenAI, API-submitted data is not used to train OpenAI's
                models
              </li>
              <li>
                OpenAI's data handling practices are governed by{' '}
                <a
                  href="https://openai.com/policies/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#10A7F3] hover:underline"
                >
                  OpenAI's Privacy Policy
                </a>{' '}
                and{' '}
                <a
                  href="https://openai.com/policies/api-data-usage-policies"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#10A7F3] hover:underline"
                >
                  API data usage policies
                </a>
              </li>
            </ul>
            <p className="font-semibold text-[#1e293b] mt-3">No other AI providers</p>
            <p>FlowSync does not use any other AI provider for email processing.</p>
            <p className="font-semibold text-[#1e293b] mt-3">We do not use third-party analytics or advertising tracking services at this time.</p>
            <p>
              FlowSync does not use Google Analytics, Mixpanel, Amplitude, Segment, Sentry, or any
              third-party analytics or error-tracking service. No tracking pixels, advertising SDKs,
              or behavioral analytics tools are present in the application.
            </p>
            <p className="font-semibold text-[#1e293b] mt-3">System email</p>
            <p>
              We use an SMTP mail service to send transactional emails such as beta approval
              notifications. These emails contain only your email address and a link to the FlowSync
              application.
            </p>
          </Section>

          <Divider />

          {/* 6. Data Storage & Infrastructure */}
          <Section number="6" title="Data Storage & Infrastructure">
            <div className="mt-1">
              <DataTable
                headers={['Component', 'Technology', 'What Is Stored There']}
                rows={[
                  ['Primary database', 'SQLite (persistent disk)', 'All user, email, task, draft, and operational data'],
                  ['Task queue / cache', 'Redis', 'Background job coordination; no email content persisted'],
                  ['Application hosting', 'Render', 'Application code and runtime'],
                  ['System email', 'SMTP over TLS (port 587)', 'Transactional emails only'],
                ]}
              />
            </div>
            <p className="font-semibold text-[#1e293b] mt-4">Encryption at rest</p>
            <p>
              OAuth access tokens and refresh tokens are encrypted in the database using Fernet
              symmetric encryption (AES-128-CBC + HMAC-SHA256). The encryption key is derived from
              the application's secret key using PBKDF2-HMAC-SHA256 with 100,000 iterations. The
              Google OAuth client secret is never stored in the database — it is read from a
              server-side environment variable at runtime only.
            </p>
            <p className="font-semibold text-[#1e293b] mt-3">Encryption in transit</p>
            <p>
              All communications between your browser and FlowSync are encrypted using TLS (HTTPS).
              HTTP requests are automatically redirected to HTTPS in production. HTTP Strict
              Transport Security (HSTS) is enforced with a one-year policy including subdomains.
            </p>
          </Section>

          <Divider />

          {/* 7. Data Retention */}
          <Section number="7" title="Data Retention">
            <div className="mt-1">
              <DataTable
                headers={['Data Type', 'Retention Period']}
                rows={[
                  ['Synced email data (messages, threads, labels)', 'Until you disconnect your account or request deletion'],
                  ['AI-generated tasks and drafts', 'Until you delete them or purge your account'],
                  ['Archived tasks and drafts', '30 days after archiving, then automatically deleted'],
                  ['OAuth tokens', 'Deleted immediately upon Gmail disconnection'],
                  ['Sync logs and scan job records', 'Until account deletion'],
                  ['Beta access requests', 'Until account deletion'],
                ]}
              />
            </div>
            <p className="font-semibold text-[#1e293b] mt-4">Account deletion</p>
            <p>
              When your FlowSync account is deleted, all associated data is permanently removed from
              our database, including all synced emails, tasks, drafts, OAuth tokens, sync logs, and
              account preferences. Deletion is immediate and irreversible.
            </p>
          </Section>

          <Divider />

          {/* 8. Your Rights & Controls */}
          <Section number="8" title="Your Rights & Controls">
            <p>You have the following rights with respect to your data:</p>

            <div className="mt-3 space-y-3">
              <div className="rounded-xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur">
                <p className="font-semibold text-[#1e293b] text-sm">Right to Access</p>
                <p className="mt-1 text-sm leading-6">
                  You can view your synced emails, generated tasks, and reply drafts directly within
                  the FlowSync application.
                </p>
              </div>

              <div className="rounded-xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur">
                <p className="font-semibold text-[#1e293b] text-sm">Right to Delete Email Data</p>
                <p className="mt-1 text-sm leading-6">
                  You can permanently delete all synced email data, generated tasks, reply drafts,
                  and your Gmail connection from FlowSync at any time using the{' '}
                  <span className="font-medium text-[#0D8ED3]">"Disconnect and Erase All Data"</span>{' '}
                  feature in the application settings. This action immediately revokes your Google
                  OAuth token, deletes all stored credentials, and permanently removes all synced
                  emails, tasks, and drafts associated with your account.
                </p>
              </div>

              <div className="rounded-xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur">
                <p className="font-semibold text-[#1e293b] text-sm">Right to Disconnect Gmail</p>
                <p className="mt-1 text-sm leading-6">
                  You can revoke FlowSync's access to your Gmail account at any time without deleting
                  your FlowSync account. After disconnection, no further email syncing occurs.
                  Previously synced data remains in our database until you request its deletion.
                </p>
              </div>

              <div className="rounded-xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur">
                <p className="font-semibold text-[#1e293b] text-sm">Right to Correction</p>
                <p className="mt-1 text-sm leading-6">
                  If your account information is incorrect, you can update it through your account
                  settings.
                </p>
              </div>

              <div className="rounded-xl border border-white/60 bg-white/60 p-4 shadow-sm backdrop-blur">
                <p className="font-semibold text-[#1e293b] text-sm">Right to Account Deletion</p>
                <p className="mt-1 text-sm leading-6">
                  You may request full account deletion by contacting us at the address listed in
                  Section 12. Upon verified request, we will delete your account and all associated
                  data.
                </p>
              </div>
            </div>
          </Section>

          <Divider />

          {/* 9. Security Practices */}
          <Section number="9" title="Security Practices">
            <p>We implement the following technical and organizational security measures:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>
                <span className="font-semibold text-[#1e293b]">HTTPS enforcement:</span> All traffic
                is served over HTTPS. HTTP requests are automatically redirected. HSTS is configured
                with a one-year duration, including subdomains and preload.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Secure cookies:</span> Session and
                CSRF cookies are set with the Secure flag in production, preventing transmission
                over unencrypted connections.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Token encryption:</span> OAuth access
                and refresh tokens are encrypted at rest using Fernet (AES-128-CBC) with
                PBKDF2-derived keys. The client secret is never stored in the database.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Automatic token revocation:</span> If
                Google reports that an OAuth token has been revoked or has expired permanently,
                FlowSync automatically disables the associated Gmail connection.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Rate limiting:</span> Inbox scan
                operations are rate-limited per user account to prevent abuse.
              </li>
              <li>
                <span className="font-semibold text-[#1e293b]">Access controls:</span> Users can
                only access their own data. Administrative functions are restricted to staff accounts.
              </li>
            </ul>
            <p className="mt-3">
              No security system is perfect. In the event of a data breach affecting your personal
              information, we will notify affected users in accordance with applicable law.
            </p>
          </Section>

          <Divider />

          {/* 10. Children's Privacy */}
          <Section number="10" title="Children's Privacy">
            <p>
              Flowman and FlowSync are not directed at children under the age of 13. We do not
              knowingly collect personal information from children under 13. If you believe a child
              under 13 has provided us with personal information, please contact us and we will
              promptly delete it.
            </p>
          </Section>

          <Divider />

          {/* 11. Changes to This Policy */}
          <Section number="11" title="Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we
              will update the "Last Updated" date at the top of this document. For significant
              changes, we may notify you by email or via a notice within the application.
            </p>
            <p>
              Your continued use of Flowman and FlowSync after a policy update constitutes your
              acceptance of the updated policy.
            </p>
          </Section>

          <Divider />

          {/* 12. Contact */}
          <Section number="12" title="Contact Us">
            <p>
              If you have questions, concerns, or requests regarding this Privacy Policy or your
              personal data, please contact us at:
            </p>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-[#10A7F3]/5 px-4 py-2 text-sm font-medium text-[#0D8ED3]">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
              flowman.app@gmail.com
            </div>
            <p className="mt-3">
              We will respond to privacy-related inquiries within a reasonable timeframe.
            </p>
          </Section>

          {/* Back link */}
          <div className="mt-8 flex justify-end">
            <a
              href="/"
              className="rounded-full bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-[#10A7F3]/30 transition-all hover:-translate-y-0.5 hover:shadow-xl"
            >
              Back to Home
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
